import React, { useState } from 'react';
import axios from 'axios';
import { exportCSV, exportDatabase, getAllAgents } from '../services/api';

const Export = () => {
  const [loading, setLoading] = useState({ csv: false, db: false, cartes: false });
  const [message, setMessage] = useState({ type: '', text: '' });

  const API_URL = process.env.REACT_APP_API_URL || 'https://idsysbackend.onrender.com/api';

  const handleExportCSV = async () => {
    setLoading({ ...loading, csv: true });
    setMessage({ type: '', text: '' });
    try {
      await exportCSV();
      setMessage({ type: 'success', text: 'Export CSV réussi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'export CSV' });
    } finally {
      setLoading({ ...loading, csv: false });
    }
  };

  const handleExportDatabase = async () => {
    setLoading({ ...loading, db: true });
    setMessage({ type: '', text: '' });
    try {
      const response = await exportDatabase();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${Date.now()}.sqlite.aes`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Export base de données réussi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'export de la base de données' });
    } finally {
      setLoading({ ...loading, db: false });
    }
  };

  const handleGenerateAllCards = async () => {
    if (!window.confirm('Générer les cartes PDF pour tous les agents ?\nCela peut prendre du temps.')) {
      return;
    }

    setLoading({ ...loading, cartes: true });
    setMessage({ type: '', text: '' });

    try {
      const agents = await getAllAgents();
      let successCount = 0;
      let errorCount = 0;

      for (const agent of agents) {
        try {
          await axios.post(`${API_URL}/agents/generate-carte`, agent);
          successCount++;
        } catch (error) {
          console.error(`Erreur pour ${agent.matricule}:`, error);
          errorCount++;
        }
      }

      setMessage({
        type: successCount > 0 ? 'success' : 'error',
        text: `Génération terminée: ${successCount} réussie(s), ${errorCount} erreur(s)`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la génération des cartes' });
    } finally {
      setLoading({ ...loading, cartes: false });
    }
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-text mb-8 flex items-center">
          <i className="fas fa-file-export mr-3 text-primary"></i>
          Export de Données
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Export CSV */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <i className="fas fa-file-csv text-3xl text-primary mr-4"></i>
              <div>
                <h2 className="text-xl font-semibold text-text">Export CSV</h2>
                <p className="text-sm text-text-light">Exporter tous les agents en format CSV</p>
              </div>
            </div>
            <button
              onClick={handleExportCSV}
              disabled={loading.csv}
              className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading.csv ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>Export en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-download mr-2"></i>Exporter CSV
                </>
              )}
            </button>
          </div>

          {/* Export Database */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <i className="fas fa-database text-3xl text-primary mr-4"></i>
              <div>
                <h2 className="text-xl font-semibold text-text">Export Base de Données</h2>
                <p className="text-sm text-text-light">Exporter la base de données cryptée (AES)</p>
              </div>
            </div>
            <button
              onClick={handleExportDatabase}
              disabled={loading.db}
              className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading.db ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>Export en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-download mr-2"></i>Exporter DB
                </>
              )}
            </button>
          </div>

          {/* Generate All Cards */}
          <div className="bg-cardBg rounded-lg shadow-lg p-6 md:col-span-2">
            <div className="flex items-center mb-4">
              <i className="fas fa-id-card text-3xl text-primary mr-4"></i>
              <div>
                <h2 className="text-xl font-semibold text-text">Générer Toutes les Cartes</h2>
                <p className="text-sm text-text-light">
                  Générer les cartes PDF pour tous les agents enregistrés
                </p>
              </div>
            </div>
            <button
              onClick={handleGenerateAllCards}
              disabled={loading.cartes}
              className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading.cartes ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>Génération en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf mr-2"></i>Générer Toutes les Cartes PDF
                </>
              )}
            </button>
          </div>
        </div>

        {message.text && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-700 border border-green-400'
                : 'bg-red-100 text-red-700 border border-red-400'
            }`}
          >
            <i
              className={`fas ${
                message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
              } mr-2`}
            ></i>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default Export;

